import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { Image } from '@archipro/rocco/components/image';
import type { TagType } from '@modules/product-tagging/type';
import { Link } from 'react-router';
import { useRef } from 'react';
import { isUserLoggedIn, useUser } from '~/modules/user/hook/use-user';

interface TagHoverContentProps {
    bottomActions?: React.ReactNode[];
    showImageOverlay?: boolean;
    marker: TagType;
}

export const TagHoverContent = ({
    marker,
    bottomActions,
    showImageOverlay = false,
}: TagHoverContentProps) => {
    const { product } = marker;

    const tracker = useTracker();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);

    const ref = useRef<HTMLDivElement | null>(null);
    useLogImpression({
        data: {
            professionalID: product?.professionalID ?? 0,
            itemID: product?.id ?? 0,
            type: isLoggedIn ? 'ProductTagLoggedIn' : 'ProductTagLoggedOut',
        },
        ref,
    });

    if (!product) {
        return null;
    }
    const onClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        tracker.log('TagHoverClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: product.id,
                professionalID: product.professionalID,
                tagID: product.tagId,
            },
        });
        window.open(product.link, '_blank');
    };

    const { imageUrl, title, professional } = product;

    return (
        <div
            ref={ref}
            className="relative mx-2 flex flex-col items-center gap-3.5 overflow-hidden rounded-lg bg-gray-50/70 p-3 shadow-[inset_0px_0px_4px_0px_rgba(0,0,0,0.25)] backdrop-blur-[5px]"
        >
            {showImageOverlay && (
                <div className="absolute left-0 top-0 z-fixed flex w-full flex-col gap-1 bg-black/50 px-3 py-2">
                    <span className="text-xs font-medium text-white">
                        {marker.member
                            ? `${marker.member.firstName} ${marker.member.lastName}`
                            : `Deleted user`}
                    </span>
                    <span className="text-xs text-white">
                        {marker.member ? `${marker.member.email}` : ''}
                    </span>
                </div>
            )}
            <div className="group/img relative flex w-52 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-50">
                <Link
                    to={product.link}
                    onClick={onClick}
                    className="transition-transform duration-300 group-hover/img:scale-105"
                >
                    <Image
                        src={imageUrl ?? ''}
                        alt={title}
                        width={208}
                        height={208}
                    />
                </Link>

                {showImageOverlay && bottomActions && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-2">
                        {bottomActions.map((action) => action)}
                    </div>
                )}
            </div>
            <div className="flex w-52 flex-col gap-0.5">
                <Link
                    to={product.link}
                    className="w-full truncate text-3.5 font-medium"
                    onClick={onClick}
                >
                    <span className="hover:underline">{title}</span>
                </Link>
                {product.link && (
                    <Link
                        className="w-full truncate text-3 text-gray-500"
                        to={product.link}
                        onClick={onClick}
                    >
                        <span className="hover:underline">{professional}</span>
                    </Link>
                )}
            </div>
        </div>
    );
};
